/**
 * Market data functionality for Alpaca API
 */

import { makeDataRequest } from './client';
import { cached } from '../cache';

/**
 * Interfaces for market data types
 */
export interface Quote {
  askPrice: number;
  askSize: number;
  bidPrice: number;
  bidSize: number;
  timestamp: string;
}

export interface Trade {
  price: number;
  size: number;
  timestamp: string;
  exchange: string;
  id: number;
}

export interface Bar {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  vwap?: number;
  n?: number;
}

export interface QuoteContext {
  price: number;
  ask: number;
  bid: number;
  volume?: number;
  timestamp: string;
}

export type TimeFrame = '1Min' | '5Min' | '15Min' | '1Hour' | '1Day' | '1Week' | '1Month';

export interface HistoricalBarsParams {
  symbols: string | string[];
  timeframe: TimeFrame;
  start?: string;
  end?: string;
  limit?: number;
  adjustment?: 'raw' | 'split' | 'dividend' | 'all';
  page_token?: string;
  feed?: 'iex' | 'sip';
}

/**
 * Get the latest quote for a symbol
 */
export async function getLatestQuote(symbol: string): Promise<QuoteContext> {
  const quote = await makeDataRequest<any>('GET', `/stocks/${symbol}/quotes/latest`);
  
  // Handle potential differences in API response structure
  // Safely convert timestamp
  let timestamp;
  try {
    if (quote?.quote?.t) {
      // Alpaca timestamps are in nanoseconds or milliseconds depending on the endpoint
      // Try to handle both formats by checking the size of the number
      const timeValue = typeof quote.quote.t === 'number' ? quote.quote.t : parseInt(quote.quote.t, 10);
      const timestampMs = timeValue > 9999999999 ? timeValue / 1000000 : timeValue * 1000;
      timestamp = new Date(timestampMs).toISOString();
    } else if (quote?.Last?.Timestamp) {
      timestamp = quote.Last.Timestamp;
    } else {
      timestamp = new Date().toISOString();
    }
  } catch (e) {
    console.warn('Error parsing timestamp, using current time:', e);
    timestamp = new Date().toISOString();
  }

  // Log the response structure to help with debugging
  console.log(`Quote data for ${symbol}:`, JSON.stringify(quote, null, 2));
  
  return {
    price: quote?.quote?.ap ?? quote?.Last?.AskPrice ?? quote?.ap ?? 0,
    ask: quote?.quote?.ap ?? quote?.Last?.AskPrice ?? quote?.ask ?? quote?.askprice ?? 0,
    bid: quote?.quote?.bp ?? quote?.Last?.BidPrice ?? quote?.bid ?? quote?.bidprice ?? 0,
    volume: quote?.quote?.s ?? quote?.Last?.Size ?? quote?.volume ?? undefined,
    timestamp,
  };
}

/**
 * Get quotes for multiple symbols
 */
export async function getRealTimeContext(symbols: string[]): Promise<Record<string, QuoteContext>> {
  const sanitized = symbols.map((s) => s.trim().toUpperCase()).filter(Boolean);

  const entries = await Promise.all(
    sanitized.map(async (sym) => {
      return [sym, await cached(`quote-${sym}`, () => getLatestQuote(sym))] as const;
    }),
  );

  return Object.fromEntries(entries);
}

/**
 * Get the latest trade for a symbol
 */
export async function getLatestTrade(symbol: string): Promise<Trade> {
  return makeDataRequest<Trade>('GET', `/stocks/${symbol}/trades/latest`);
}

/**
 * Get historical bars for a symbol or multiple symbols
 */
export async function getHistoricalBars(params: HistoricalBarsParams): Promise<Record<string, Bar[]>> {
  const { symbols, ...queryParams } = params;
  
  // Convert single symbol to array for consistent handling
  const symbolsArray = Array.isArray(symbols) ? symbols : [symbols];
  const symbolsParam = symbolsArray.join(',');
  
  return makeDataRequest<Record<string, Bar[]>>('GET', `/stocks/bars`, {
    symbols: symbolsParam,
    ...queryParams,
  });
}

/**
 * Get snapshot of latest quotes and trades for multiple symbols
 */
export async function getMarketSnapshot(symbols: string[]): Promise<any> {
  const symbolsParam = symbols.join(',');
  return makeDataRequest<any>('GET', `/stocks/snapshots`, { symbols: symbolsParam });
}